import type { UseUserScopesReturn } from '&/hooks/use-user-scopes';

/** What {@link isTerritoryScopedAdmin} reads: the user's scope flags and whether they are known yet. */
export type TerritoryAdminScopeInput = Pick<
  UseUserScopesReturn,
  'canAccessTenantSettings' | 'canAccessTerritories' | 'isTenantScoped'
> & {
  /**
   * True once `/users/me` and the permissions have both loaded. Until then `isTenantScoped` reads false for
   * everyone, so a tenant-wide Fleet Manager would look like a Territory Admin; the answer stays false
   * (fail-closed) and callers that must not act on a wrong answer wait for this flag.
   */
  isScopeResolved: boolean;
};

/**
 * True for a Territory Admin (SWOOP-302): a user who administers territories (`territories:list`) without
 * the tenant-wide settings (`tenants:read`) and whose scopes are territory-level, not tenant-wide.
 *
 * The tenant-scoped check is what keeps a Fleet Manager out: it also holds `territories:list` without
 * `tenants:read`, but works across the whole tenant, so its Settings stay as they are.
 *
 * A pure function with no imports at runtime, so the sidebar config can use it without pulling in the auth
 * context (and Firebase) through a hook.
 */
export function isTerritoryScopedAdmin(input: TerritoryAdminScopeInput): boolean {
  return input.isScopeResolved && input.canAccessTerritories && !input.canAccessTenantSettings && !input.isTenantScoped;
}
